import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Calendar, Award, Car, BookOpen, Shield, Users, BookCheck, TrendingUp, MapPin, Star } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useFirebase } from "@/contexts/FirebaseContext";
import { collection, getDocs, query, where, orderBy, limit } from "firebase/firestore";

interface Course {
  id: string;
  title: string;
  description: string;
  price: number;
  type: string;
  subtype: string;
  pricePerHour?: number;
  totalHours?: number;
  duration?: number;
  published: boolean;
}

interface StudentStory {
  id: string;
  studentName: string;
  instructorName: string;
  rating: number;
  description: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: any;
}

const Home = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [stories, setStories] = useState<StudentStory[]>([]);
  const [loadingStories, setLoadingStories] = useState(true);
  const { getFirestore } = useFirebase();

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const db = getFirestore();
        const coursesCollection = collection(db, "courses");
        const coursesSnapshot = await getDocs(coursesCollection);
        const coursesData = coursesSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          } as Course))
          .filter(course => course.published);
        setCourses(coursesData);
      } catch (error) {
        console.error("Error fetching courses:", error);
      }
    };

    const fetchStories = async () => {
      try {
        setLoadingStories(true);
        console.log("Fetching stories from Firestore...");
        const db = getFirestore();
        const storiesQuery = query(
          collection(db, "student_stories"),
          where("status", "==", "approved"),
          orderBy("createdAt", "desc"),
          limit(6)
        );
        
        console.log("Executing stories query...");
        const storiesSnapshot = await getDocs(storiesQuery);
        console.log("Stories snapshot:", storiesSnapshot.docs.length, "documents found");
        
        const storiesData = storiesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as StudentStory[];
        
        console.log("Processed stories data:", storiesData);
        setStories(storiesData);
      } catch (error) {
        console.error("Error fetching stories:", error);
      } finally {
        setLoadingStories(false);
      }
    };

    fetchCourses();
    fetchStories();
  }, [getFirestore]);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  // Helper function to get course image based on type
  const getCourseImage = (type: string, subtype: string) => {
    if (type === "practical") {
      if (subtype === "individual") {
        // Individual lessons - showing instructor teaching
        return "https://images.unsplash.com/photo-1580273916550-e323be2ae537?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80";
      } else if (subtype === "practical") {
        // Car hire - showing car front view
        return "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80";
      } else {
        // Block lessons - showing student learning
        return "https://images.unsplash.com/photo-1600320254374-ce2d293c324e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80";
      }
    }
    // Theory course - showing study materials
    return "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80";
  };

  // Helper function to get course features
  const getCourseFeatures = (type: string, subtype: string) => {
    if (subtype === "individual") {
      return [
        "Flexible scheduling",
        "One-on-one instruction",
        "Learn at your own pace",
        "Custom learning plan"
      ];
    } else if (subtype === "practical") {
      return [
        "3-hour dedicated sessions",
        "Fully insured vehicle",
        "Intensive practice",
        "Perfect for test preparation"
      ];
    } else {
      return [
        "10-hour comprehensive package",
        "Structured learning plan",
        "Progress tracking",
        "Test preparation included"
      ];
    }
  };

  // Helper function to render stars
  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? "text-yellow-400 fill-current" : "text-gray-300"
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <Layout>
      {/* Hero Section with Gradient Background */}
      <section className="bg-gradient-to-r from-drivewise-900 via-blue-800 to-drivewise-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <motion.div 
            className="flex flex-col md:flex-row items-center"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.2
                }
              }
            }}
          >
            <motion.div 
              className="md:w-1/2 mb-10 md:mb-0 md:pr-10"
              variants={fadeIn}
            >
              <div className="flex items-center mb-4">
                <img 
                  src="/lovable-uploads/719484f4-0730-46ce-81b1-42c4a08b3b01.png" 
                  alt="WAY2LEARN DRIVING SCHOOL" 
                  className="h-24 mr-4 animate-pulse" 
                />
                <h1 className="text-3xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white via-blue-100 to-white">
                  WAY2LEARN DRIVING SCHOOL
                </h1>
              </div>
              <p className="text-xl mb-8 text-blue-100">
                Your trusted partner in driver training, located in the heart of London. Professional instructors, flexible scheduling, and comprehensive learning resources to help you become a safe and confident driver.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" asChild className="bg-gradient-to-r from-caution-500 to-orange-600 hover:from-caution-600 hover:to-orange-700 text-white transition-all duration-300 transform hover:scale-105">
                  <Link to="/register">Get Started</Link>
                </Button>
                <Button size="lg" asChild className="bg-gradient-to-r from-blue-600 to-drivewise-600 hover:from-blue-700 hover:to-drivewise-700 text-white transition-all duration-300 transform hover:scale-105">
                  <Link to="/book-driving-lesson">Book Driving Lesson</Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="bg-transparent text-white border-white hover:bg-white/10 transition-all duration-300 transform hover:scale-105">
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </motion.div>
            <motion.div 
              className="md:w-1/2"
              variants={fadeIn}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80" 
                alt="Student driving with instructor" 
                className="rounded-lg shadow-2xl w-full border-4 border-white/20"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section with Card Design */}
      <section className="bg-gradient-to-b from-white to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <motion.div 
              className="bg-white p-6 rounded-xl shadow-lg text-center border-t-4 border-drivewise-500"
              whileHover={{ y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <p className="text-4xl font-bold text-drivewise-600 mb-2">95%</p>
              <p className="text-lg text-road-600">Pass Rate</p>
            </motion.div>
            <motion.div 
              className="bg-white p-6 rounded-xl shadow-lg text-center border-t-4 border-blue-500"
              whileHover={{ y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <p className="text-4xl font-bold text-blue-600 mb-2">15+</p>
              <p className="text-lg text-road-600">Years Experience</p>
            </motion.div>
            <motion.div 
              className="bg-white p-6 rounded-xl shadow-lg text-center border-t-4 border-indigo-500"
              whileHover={{ y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <p className="text-4xl font-bold text-indigo-600 mb-2">50+</p>
              <p className="text-lg text-road-600">Qualified Instructors</p>
            </motion.div>
            <motion.div 
              className="bg-white p-6 rounded-xl shadow-lg text-center border-t-4 border-purple-500"
              whileHover={{ y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <p className="text-4xl font-bold text-purple-600 mb-2">10k+</p>
              <p className="text-lg text-road-600">Successful Students</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="bg-gradient-to-b from-blue-50 to-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-4xl font-bold text-road-900 mb-4 relative inline-block">
              Our Courses
              <span className="absolute -bottom-2 left-1/4 right-1/4 h-1 bg-gradient-to-r from-drivewise-500 to-blue-500 rounded-full"></span>
            </h2>
            <p className="text-xl text-road-600 max-w-3xl mx-auto mt-6">
              Choose from our range of comprehensive driving courses designed to suit your needs and schedule.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {courses.map((course) => (
              <motion.div
                key={course.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="h-full flex flex-col overflow-hidden transform transition-all duration-300 hover:shadow-lg">
                  <div className="h-48 overflow-hidden">
                    <img
                      src={getCourseImage(course.type, course.subtype)}
                      alt={course.title}
                      className="w-full h-full object-cover transform transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{course.description}</CardTitle>
                    <CardDescription>
                      {course.subtype === "individual" 
                        ? `£${course.pricePerHour}/hour - Individual Lessons`
                        : `£${course.price} - ${course.totalHours || course.duration} hours`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                      {course.type === "practical" ? (
                        <Car className="h-5 w-5 text-drivewise-500" />
                      ) : (
                        <BookOpen className="h-5 w-5 text-drivewise-500" />
                      )}
                      <span>
                        {course.subtype === "individual" 
                          ? "Flexible Hours"
                          : `${course.totalHours || course.duration} Hours`}
                      </span>
                    </div>
                    <ul className="space-y-2">
                      {getCourseFeatures(course.type, course.subtype).map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-600">
                          <Check className="h-4 w-4 text-green-500 mr-2" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <div className="p-6 pt-0 mt-auto">
                    <Button asChild className="w-full">
                      <Link to={`/student-dashboard/courses`}>
                        Learn More
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section - Moved down */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-4xl font-bold text-road-900 mb-4 relative inline-block">
              Our Services
              <span className="absolute -bottom-2 left-1/4 right-1/4 h-1 bg-gradient-to-r from-drivewise-500 to-blue-500 rounded-full"></span>
            </h2>
            <p className="text-xl text-road-600 max-w-3xl mx-auto mt-6">
              We offer comprehensive driving education with a variety of services to meet your needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div 
              whileHover={{ y: -10, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }} 
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Card className="overflow-hidden border-0 bg-gradient-to-br from-white to-blue-50 shadow-xl">
                <div className="h-2 bg-gradient-to-r from-drivewise-500 to-drivewise-600"></div>
                <CardHeader>
                  <div className="bg-gradient-to-br from-drivewise-100 to-blue-100 p-3 rounded-full w-16 h-16 flex items-center justify-center mb-4">
                    <Car className="h-8 w-8 text-drivewise-600" />
                  </div>
                  <CardTitle className="text-xl">Driving Lessons</CardTitle>
                  <CardDescription className="text-base">
                    Personalized driving lessons tailored to your skill level and learning pace.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="bg-green-100 p-1 rounded-full mr-2">
                        <Check className="h-5 w-5 text-green-600" />
                      </div>
                      <span>One-on-one instruction</span>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-green-100 p-1 rounded-full mr-2">
                        <Check className="h-5 w-5 text-green-600" />
                      </div>
                      <span>Flexible scheduling</span>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-green-100 p-1 rounded-full mr-2">
                        <Check className="h-5 w-5 text-green-600" />
                      </div>
                      <span>Modern, dual-control vehicles</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div 
              whileHover={{ y: -10, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }} 
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Card className="overflow-hidden border-0 bg-gradient-to-br from-white to-blue-50 shadow-xl">
                <div className="h-2 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
                <CardHeader>
                  <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-3 rounded-full w-16 h-16 flex items-center justify-center mb-4">
                    <BookOpen className="h-8 w-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-xl">Theory Test Prep</CardTitle>
                  <CardDescription className="text-base">
                    Comprehensive preparation for your theory test with practice exams and study materials.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="bg-green-100 p-1 rounded-full mr-2">
                        <Check className="h-5 w-5 text-green-600" />
                      </div>
                      <span>Online practice tests</span>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-green-100 p-1 rounded-full mr-2">
                        <Check className="h-5 w-5 text-green-600" />
                      </div>
                      <span>Hazard perception training</span>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-green-100 p-1 rounded-full mr-2">
                        <Check className="h-5 w-5 text-green-600" />
                      </div>
                      <span>Study guides and resources</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div 
              whileHover={{ y: -10, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }} 
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <Card className="overflow-hidden border-0 bg-gradient-to-br from-white to-blue-50 shadow-xl">
                <div className="h-2 bg-gradient-to-r from-indigo-500 to-purple-500"></div>
                <CardHeader>
                  <div className="bg-gradient-to-br from-indigo-100 to-purple-100 p-3 rounded-full w-16 h-16 flex items-center justify-center mb-4">
                    <Calendar className="h-8 w-8 text-indigo-600" />
                  </div>
                  <CardTitle className="text-xl">Intensive Courses</CardTitle>
                  <CardDescription className="text-base">
                    Fast-track your learning with our intensive driving courses designed for quick results.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="bg-green-100 p-1 rounded-full mr-2">
                        <Check className="h-5 w-5 text-green-600" />
                      </div>
                      <span>Condensed learning periods</span>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-green-100 p-1 rounded-full mr-2">
                        <Check className="h-5 w-5 text-green-600" />
                      </div>
                      <span>Tailored to your experience</span>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-green-100 p-1 rounded-full mr-2">
                        <Check className="h-5 w-5 text-green-600" />
                      </div>
                      <span>Practical test booking included</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section - New Addition */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-4xl font-bold text-road-900 mb-4 relative inline-block">
              Why Choose Us
              <span className="absolute -bottom-2 left-1/4 right-1/4 h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"></span>
            </h2>
            <p className="text-xl text-road-600 max-w-3xl mx-auto mt-6">
              Experience the Way2Learn difference with our unique approach to driver education
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Safety First", description: "Our priority is teaching safe driving habits that last a lifetime", color: "from-blue-100 to-blue-200", textColor: "text-blue-600" },
              { icon: Users, title: "Expert Instructors", description: "Learn from certified instructors with years of experience", color: "from-green-100 to-green-200", textColor: "text-green-600" },
              { icon: BookCheck, title: "Comprehensive Courses", description: "Theory and practical lessons designed for complete learning", color: "from-amber-100 to-amber-200", textColor: "text-amber-600" },
              { icon: TrendingUp, title: "High Success Rate", description: "95% first-time pass rate among our students", color: "from-purple-100 to-purple-200", textColor: "text-purple-600" },
            ].map((item, index) => (
              <motion.div 
                key={index}
                className="text-center p-6 rounded-xl bg-white shadow-lg border border-gray-100"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ y: -5, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
              >
                <div className={`mx-auto w-16 h-16 bg-gradient-to-br ${item.color} rounded-full flex items-center justify-center mb-4`}>
                  <item.icon className={`h-8 w-8 ${item.textColor}`} />
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations Section - Updated */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-4xl font-bold text-road-900 mb-4 relative inline-block">
              Postal Location
              <span className="absolute -bottom-2 left-1/4 right-1/4 h-1 bg-gradient-to-r from-drivewise-500 to-blue-500 rounded-full"></span>
            </h2>
            <p className="text-xl text-road-600 max-w-3xl mx-auto mt-6">
              Visit our office in central London
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <motion.div 
              className="bg-white p-8 rounded-xl shadow-lg"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center mb-6">
                <MapPin className="h-8 w-8 text-drivewise-600 mr-4" />
                <h3 className="text-2xl font-bold text-road-900">Our Location</h3>
              </div>
              <p className="text-lg text-road-700 mb-4">
                167 - 169 Great Portland Street<br />
                5th Floor<br />
                London<br />
                W1W 5PF
              </p>
              <p className="text-lg text-road-700 mb-6">
                Phone: +442034889598<br />
                Email: info@way2learn.co.uk
              </p>
              <Button asChild className="w-full">
                <Link to="/contact">Contact Us</Link>
              </Button>
            </motion.div>
            
            <motion.div 
              className="rounded-xl overflow-hidden shadow-lg"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1464938050520-ef2270bb8ce8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1474&q=80" 
                alt="Our Location" 
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-gradient-to-b from-white to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-4xl font-bold text-road-900 mb-4 relative inline-block">
              Student Success Stories
              <span className="absolute -bottom-2 left-1/4 right-1/4 h-1 bg-gradient-to-r from-drivewise-500 to-blue-500 rounded-full"></span>
            </h2>
            <p className="text-xl text-road-600 max-w-3xl mx-auto mt-6">
              Hear from our graduates about their experience with Way2Learn Driving School.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {loadingStories ? (
              // Loading state
              Array.from({ length: 3 }).map((_, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Card className="h-full border border-gray-200 overflow-hidden bg-gradient-to-b from-white to-blue-50">
                    <CardContent className="pt-6 h-full flex flex-col">
                      <div className="flex items-center mb-4">
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 rounded-full bg-gray-200 animate-pulse"></div>
                        </div>
                        <div className="ml-4 flex-1">
                          <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg flex-grow">
                        <div className="space-y-2">
                          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                          <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            ) : stories.length === 0 ? (
              // No stories available
              <div className="col-span-full text-center py-12">
                <div className="text-gray-500">
                  <p className="text-lg font-medium mb-2">No stories available yet</p>
                  <p className="text-sm">Check back soon for student success stories!</p>
                </div>
              </div>
            ) : (
              // Display actual stories
              stories.map((story, index) => (
                <motion.div
                  key={story.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                >
                  <Card className="h-full border border-gray-200 hover:border-blue-300 transition-all duration-300 overflow-hidden bg-gradient-to-b from-white to-blue-50">
                    <CardContent className="pt-6 h-full flex flex-col">
                      <div className="flex items-center mb-4">
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center ring-2 ring-blue-300">
                            <span className="text-blue-600 font-semibold text-lg">
                              {story.studentName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4 flex-1">
                          <h3 className="text-lg font-medium text-road-900">{story.studentName}</h3>
                          <p className="text-blue-500 text-sm">Instructor: {story.instructorName}</p>
                          <div className="mt-1">
                            {renderStars(story.rating)}
                          </div>
                        </div>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg flex-grow">
                        <p className="text-road-700 italic">"{story.description}"</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-drivewise-800 via-blue-800 to-drivewise-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold mb-6">Ready to Start Your Driving Journey?</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto text-blue-100">
              Join thousands of satisfied students who have successfully learned to drive with Way2Learn Driving School.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
                <Button size="lg" asChild className="bg-gradient-to-r from-caution-500 to-orange-600 hover:from-caution-600 hover:to-orange-700 text-white">
                  <Link to="/register">Register Now</Link>
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
                <Button size="lg" variant="outline" asChild className="bg-transparent text-white border-white hover:bg-white/10">
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
